import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';

import { Button } from '@peacecover/component';
import TradingPhotoCard from './TradingPhotoCard';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import { confirmReview, rejectReview } from '../apis/reviewStatusMutation';
import { GradientArrowsDownUp } from '../icons/GradientArrowsDownUp';
import type { ReviewType } from '../types/ReviewableType';
import type { TradingPhotoCardType } from '../../trading/types/TradingPhotoCardType';
import type { MatchedTradingPhotoCardReviewableType } from '../types/ReviewableType';

const PostReviewRequest = ({ reviewData }: { reviewData: ReviewType }) => {
  const navigate = useNavigate();

  // reviewable 타입 가드
  const isTradingPhotoCardReviewable = (
    v: ReviewType['reviewable']
  ): v is Omit<TradingPhotoCardType, 'userProfile'> => !!v && 'tradingPhotoCardId' in v;

  let postReviewTitle: string;
  if (isTradingPhotoCardReviewable(reviewData.reviewable) && reviewData.reviewable.userId) {
    postReviewTitle =
      reviewData.reviewable.userId === reviewData.reviewer.userId
        ? t('TradeReviewRequestPage.sellerPostReview')
        : t('TradeReviewRequestPage.buyerPostReview');
  } else {
    postReviewTitle = t('TradeReviewRequestPage.exchangerPostReview');
  }

  const handleAcceptReview = async () => {
    if (reviewData.reviewableType === 'MatchedTradingPhotoCard') {
      navigate(
        `/reviews/add-review?reviewerId=${reviewData.reviewerId}&reviewableId=${reviewData.reviewableId}&reviewableType=${reviewData.reviewableType}&reviewId=${reviewData.reviewId}`
      );
      return;
    }

    try {
      const response = await confirmReview(reviewData.reviewableId);
      if (response.ok) {
        navigate(
          `/reviews/add-review?reviewerId=${reviewData.reviewerId}&reviewableId=${reviewData.reviewableId}&reviewableType=${reviewData.reviewableType}&reviewId=${reviewData.reviewId}`
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRejectReview = async () => {
    try {
      const response = await rejectReview(reviewData.reviewId);
      if (response.ok) {
        navigate('/feed');
      }
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-[6px] mt-[32px]">
        <ProfileAvatar
          thumbnailUrl={reviewData.reviewer?.thumbnailUrl || reviewData.reviewer?.pictureUrl}
          widthAndHeight="32px"
          svgWidthAndHeight="32px"
        />
        <p className="text-[15px] font-semibold text-text-main-lv2 leading-none max-w-[166px] truncate">
          {reviewData.reviewer.nickname}
        </p>
      </div>
      <h1 className="text-[26px] font-semibold text-text-main-lv2 mt-[24px]">{postReviewTitle}</h1>
      <p className="text-center text-[17px] font-medium text-text-main-lv3 mt-[10px] whitespace-pre-line leading-tight">
        {t('TradeReviewRequestPage.postReviewDescription')}
      </p>
      <div className="w-full px-[24px] mt-[48px] flex flex-col items-center">
        {isTradingPhotoCardReviewable(reviewData.reviewable) ? (
          <TradingPhotoCard photoCard={reviewData.reviewable.photoCard} />
        ) : (
          <MatchedTradingPhotoCard matchedTradingPhotoCard={reviewData.reviewable} />
        )}
      </div>
      <div className="absolute bottom-[46px] flex flex-col items-center gap-[32px] w-full px-[24px]">
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          onClick={handleAcceptReview}
        >
          {t('TradeReviewRequestPage.postReviewAndViewReview')}
        </Button>
        <button
          type="button"
          onClick={handleRejectReview}
          className="text-[15px] font-medium text-text-main-lv3 leading-none"
        >
          {t('TradeReviewRequestPage.noTradeWithThisPerson')}
        </button>
      </div>
    </div>
  );
};

export default PostReviewRequest;

const MatchedTradingPhotoCard = ({
  matchedTradingPhotoCard
}: {
  matchedTradingPhotoCard: MatchedTradingPhotoCardReviewableType;
}) => {
  const { sourceCard, targetCard } = matchedTradingPhotoCard;

  return (
    <div className="w-full mt-[24px] flex flex-col items-center">
      <TradingPhotoCard
        photoCard={sourceCard.photoCard}
        source={t('TradeReviewRequestPage.me') as string}
      />
      <div className="mt-[18px] mb-[20px]">
        <GradientArrowsDownUp size={32} />
      </div>
      <TradingPhotoCard
        photoCard={targetCard.photoCard}
        source={t('TradeReviewRequestPage.other') as string}
      />
    </div>
  );
};
